import {
  AgreementInstructorDocument,
  FranchiseDocumentType,
  MyDocumentsProps,
} from "./types";
import {
  ArrowUpward,
  ChevronLeft,
  ChevronRight,
  Clear,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@material-ui/icons";
import {
  Box,
  Card,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
  colors,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { FranchiseTable } from "@hubcapp/views/MyDetails/components/FranchiseTable";
import { ModalAddNewDocument } from "./components/DocumentsTable/ModalAddNewDocument";
import { Status } from "./constants";
import { TabContext, TabPanel } from "@material-ui/lab";
import { Upload } from "react-feather";
import { format } from "date-fns";
import { useMutationUpdateAgreementDocument } from "./hooks";
import { useSnackbar } from "notistack";
import Button from "@ds-developer/button";
import Content from "@ds-developer/content";
import IconButton from "@material-ui/core/IconButton";
import Label from "@ds-developer/label";
import MaterialTable, { Icons, MTableBodyRow as Row } from "material-table";
import React, { createRef, forwardRef, useState } from "react";
import UpdateDocumentConfirmationModal from "./components/DocumentsTable/UpdateDocumentConfirmationModal";

const useStyles = makeStyles(theme => ({
  detailsWrapper: {
    [theme.breakpoints.down("sm")]: {
      marginTop: "24px",
    },
  },
  root: {
    width: "100%",
  },
  container: {
    maxHeight: 440,
  },
  buttonMain: {
    fontFamily: "Roboto",
    justifyContent: "center",
    alignItems: "center",
  },
  viewButton: {
    background: "#2A92FA",
    color: "#FFFFFF",
    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.14)",
    borderRadius: "4px",
  },
  buttonPrimary: {
    background: "#FFFFFF",
    color: "#2A92FA",
    border: "1px solid #2A92FA",
  },
  button: {
    color: colors.blueGrey[800],
    padding: "10px 8px",
    justifyContent: "flex-start",
    textTransform: "none",
    letterSpacing: 0,
    width: "100%",
  },
  expandIcon: {
    marginLeft: "auto",
    marginRight: "5px",
    height: 24,
    width: 24,
  },
  buttonRight: {
    float: "right",
  },
  labelList: {
    margin: "16px 0 16px 16px",
    fontFamily: "Roboto !important",
    fontStyle: "normal",
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "24px",
    display: "flex",
    alignItems: "center",
    letterSpacing: "-0.05px",
    color: "#263238",
  },
  franchiseLabelList: {
    fontFamily: "Roboto !important",
    fontStyle: "normal",
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: "24px",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.1px",
    color: "#263238",
  },
  buttonCollapse: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0.1px",
    color: "#263238",
  },
  actionBtn: {
    marginRight: "3rem",
  },
  customTooltip: {
    fontSize: 10,
    fontWeight: 500,
    padding: "4px 8px",
    lineHeight: "20px",
  },
  customBtn: {
    minWidth: "unset",
    "& span": {
      display: "flex",
    },
  },
  tabPanelWithTable: {
    paddingTop: 24,
    paddingBottom: 0,
    "& .MuiPaper-elevation1": {
      boxShadow: "none",
    },
  },
  tabsPanel: {
    marginTop: 20,
    marginRight: 16,
    marginLeft: 16,
  },
  title: {
    marginBottom: 24,
    fontSize: "20px",
    fontWeight: 500,
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
}));

const tableIcons: Icons = {
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const MyDocuments: React.FC<MyDocumentsProps> = ({
  refetchDocs,
  instructorId,
  loading = false,
  customerAndFranchiseDocumentsByPerson,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const mobileDevice = useMediaQuery(theme.breakpoints.down("sm"));
  const fileInputRef = createRef<HTMLInputElement>();
  const [uploadingDocument, setUploadingDocument] = useState<
    AgreementInstructorDocument
  >();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [
    uploadConfirmationModalOpened,
    setUploadConfirmationModalOpened,
  ] = useState(false);
  const [
    isModalAddNewDocumentOpened,
    setIsModalAddNewDocumentOpened,
  ] = useState(false);

  const [franchiseAgreementUpdateDocument] = useMutationUpdateAgreementDocument(
    {
      onCompleted: () => {
        setUploadingDocument(undefined);
        setIsUploading(false);
        refetchDocs();
        setUploadConfirmationModalOpened(true);
      },
      onError: () => {
        setUploadingDocument(undefined);
        setIsUploading(false);
        enqueueSnackbar("Unable to upload document", {
          variant: "error",
          role: "alert",
        });
      },
    },
  );

  const selectFileForUploading = (document: AgreementInstructorDocument) => {
    setUploadingDocument(document);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const onFileChange = (
    e: React.ChangeEvent<HTMLInputElement> & { target: { files: FileList } },
  ) => {
    if (e.target.files?.length) {
      uploadDocument(e.target.files[0]);
      return;
    }
    setUploadingDocument(undefined);
  };
  const uploadDocument = async (file: File) => {
    if (!uploadingDocument || !file) {
      setUploadingDocument(undefined);
      return;
    }
    setIsUploading(true);
    const blob = await file.arrayBuffer();
    franchiseAgreementUpdateDocument({
      variables: {
        personKey: instructorId,
        file: Buffer.from(blob),
        franAgreeDocKey: uploadingDocument.FADocumentKey,
        documentName: uploadingDocument.documentType,
        type: file.type,
      },
    });
  };

  const [value, setValue] = useState("active");
  const handleChangeTab = (event: any, newValue: string) => {
    setValue(newValue);
  };

  const activeFranchiseDocuments: FranchiseDocumentType[] = [];
  for (const franchiseDocuments of customerAndFranchiseDocumentsByPerson?.franchiseDocuments ||
    []) {
    if (franchiseDocuments.active) {
      for (const doc of franchiseDocuments?.documents) {
        activeFranchiseDocuments.push({
          ...doc,
          agreementName: franchiseDocuments?.agreementName,
          fromDate: franchiseDocuments?.fromDate,
          toDate: franchiseDocuments?.toDate,
        });
      }
      break;
    }
  }

  const inactiveFranchiseDocuments: FranchiseDocumentType[] = [];
  for (const franchiseDocuments of customerAndFranchiseDocumentsByPerson?.franchiseDocuments ||
    []) {
    if (!franchiseDocuments.active) {
      for (const doc of franchiseDocuments?.documents) {
        inactiveFranchiseDocuments.push({
          ...doc,
          agreementName: franchiseDocuments?.agreementName,
          fromDate: franchiseDocuments?.fromDate,
          toDate: franchiseDocuments?.toDate,
        });
      }
      break;
    }
  }

  return (
    <>
      <Content noLeftSpacing noRightSpacing noTopSpacing>
        <Box mb={3} className={classes.detailsWrapper}>
          <Typography className={classes.title}>Your documents</Typography>
          <MaterialTable
            isLoading={loading}
            icons={tableIcons}
            columns={[
              {
                title: "Document",
                field: "documentType",
                sorting: true,
              },
              {
                title: "Status",
                field: "status",
                sorting: true,
                render: ({ status }: AgreementInstructorDocument) => {
                  switch (status) {
                    case Status.APPROVED:
                      return <Label color="success">APPROVED</Label>;
                    case Status.REJECTED:
                      return <Label color="error">REJECTED</Label>;
                    case Status.OUTSTANDING:
                      return <Label color="warning">OUTSTANDING</Label>;
                    case Status.FOR_APPROVAL:
                      return <Label color="root">FOR APPROVAL</Label>;
                    default:
                      return "";
                  }
                },
              },
              {
                title: "Date uploaded",
                field: "uploadedDate",
                sorting: true,
                render: (rowData: AgreementInstructorDocument) =>
                  rowData.uploadedDate
                    ? format(new Date(rowData.uploadedDate), "dd/MM/yyyy")
                    : "",
              },
              {
                title: "",
                field: "status",
                sorting: true,
                width: "auto",
                render: (doc: AgreementInstructorDocument) => {
                  if (
                    doc.status === Status.OUTSTANDING ||
                    doc.status === Status.REJECTED
                  ) {
                    const buttonLoading =
                      isUploading && uploadingDocument === doc;
                    return (
                      <>
                        {mobileDevice ? (
                          <IconButton
                            size="small"
                            onClick={() => selectFileForUploading(doc)}
                            disabled={isUploading || loading}
                          >
                            {buttonLoading ? (
                              <CircularProgress size={24} />
                            ) : (
                              <Upload />
                            )}
                          </IconButton>
                        ) : (
                          <Button
                            color="primary"
                            variant="contained"
                            onClick={() => selectFileForUploading(doc)}
                            loading={buttonLoading}
                            disabled={isUploading || loading}
                          >
                            Upload
                          </Button>
                        )}
                      </>
                    );
                  }
                  return "";
                },
              },
            ].filter(
              tab => !mobileDevice || !["uploadedDate"].includes(tab.field),
            )}
            options={{
              actionsColumnIndex: -1,
              draggable: false,
              headerStyle: {
                whiteSpace: "nowrap",
              },
              paging: true,
              pageSize: 10,
              emptyRowsWhenPaging: false,
              detailPanelColumnAlignment: "right",
              tableLayout: "auto",
            }}
            localization={{
              header: { actions: "Action" },
              body: {
                emptyDataSourceMessage: loading
                  ? ""
                  : "No documents to display",
              },
            }}
            components={{
              Toolbar: () => null,
              Container: props => <Paper {...props} elevation={1} />,
              Row: props => (
                <Row
                  data-test={`learners-table-row-${props.index}`}
                  {...props}
                />
              ),
            }}
            data={
              customerAndFranchiseDocumentsByPerson?.customerDocuments || []
            }
          />
        </Box>
        <Box mb={3}>
          <Typography className={classes.title}>Franchise documents</Typography>
          <Card>
            <Tabs
              value={value}
              onChange={handleChangeTab}
              variant="scrollable"
              className={classes.tabsPanel}
              textColor="primary"
            >
              <Tab value="active" label="Active"></Tab>
              <Tab value="inactive" label="Inactive"></Tab>
            </Tabs>
            <TabContext value={value}>
              <TabPanel value="active" className={classes.tabPanelWithTable}>
                <FranchiseTable
                  loading={loading}
                  data={activeFranchiseDocuments}
                />
              </TabPanel>
              <TabPanel value="inactive" className={classes.tabPanelWithTable}>
                <FranchiseTable
                  loading={loading}
                  data={inactiveFranchiseDocuments}
                />
              </TabPanel>
            </TabContext>
          </Card>
        </Box>
      </Content>
      <ModalAddNewDocument
        isModalAddNewDocumentOpened={isModalAddNewDocumentOpened}
        setIsModalAddNewDocumentOpened={setIsModalAddNewDocumentOpened}
        personKey={instructorId}
        refetchDocs={refetchDocs}
      />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={onFileChange}
      />

      {uploadConfirmationModalOpened && (
        <UpdateDocumentConfirmationModal
          onCloseClick={() => setUploadConfirmationModalOpened(false)}
        />
      )}
    </>
  );
};

export default MyDocuments;
